import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { ButtonBase, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import WarningIcon from '@material-ui/icons/Warning'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydButton from '../../../../commonDesign/Button'
import SydInput from '../../../../commonDesign/SydInput'
import SydSelect from '../../../../commonDesign/SydSelect'
import Icon from '../../../../atoms/Icon'
import ColorPickerDialog from '../../../../atoms/ColorPickerDialog'
import IconPickerDialog from '../../../../commonDesign/IconPicker/IconPickerDialog'
import DragAndDropListItem from '../../../GroupingProvider/DragAndDropListItem'
import PlanStatusIcon from './badges/PlanStatusIcon'
import {
  findDuplicateStatusKeys,
  makeBlankStatusStyleRow,
  withDerivedOrder
} from './statusStyleHelpers'

const GRID_COLS = 'minmax(140px, 1.2fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(260px, 1.5fr) 40px'
const HEADER_GRID_COLS = '40px 52px ' + GRID_COLS

const IMAGE_MODES = {
  ID: 'imageId',
  URL: 'imageUrl'
}

const IMAGE_MODE_OPTIONS = [
  { label: 'Media ID', value: IMAGE_MODES.ID },
  { label: 'Image URL', value: IMAGE_MODES.URL }
]

// Row-local helper that swaps between the Media ID and Image URL inputs. Only
// one input is rendered at a time and the inactive field is cleared on switch
// so the persisted row matches what the admin actually sees — preventing the
// renderer's imageId-wins precedence from silently overriding a "hidden" URL.
function ImageFieldGroup ({ row, onMerge, selectClassName, inputClassName }) {
  const [mode, setMode] = useState(() => (
    row.imageUrl && !row.imageId ? IMAGE_MODES.URL : IMAGE_MODES.ID
  ))

  const handleModeChange = useCallback((e) => {
    const next = e.target.value
    setMode(next)
    if (next === IMAGE_MODES.ID) onMerge({ imageUrl: '' })
    else onMerge({ imageId: '' })
  }, [onMerge])

  return (
    <>
      <div className={selectClassName}>
        <SydSelect
          size='sm'
          minWidth='0'
          value={mode}
          onChange={handleModeChange}
          options={IMAGE_MODE_OPTIONS}
        />
      </div>
      <div className={inputClassName}>
        {mode === IMAGE_MODES.ID ? (
          <SydInput
            value={row.imageId || ''}
            onChange={(e) => onMerge({ imageId: e.target.value })}
            size='sm'
            minWidth='0'
            placeholder='Media ID'
          />
        ) : (
          <SydInput
            value={row.imageUrl || ''}
            onChange={(e) => onMerge({ imageUrl: e.target.value })}
            size='sm'
            minWidth='0'
            placeholder='https://...'
          />
        )}
      </div>
    </>
  )
}

ImageFieldGroup.propTypes = {
  row: PropTypes.object.isRequired,
  onMerge: PropTypes.func.isRequired,
  selectClassName: PropTypes.string,
  inputClassName: PropTypes.string
}

const useStyles = makeStyles(() => ({
  grid: {
    fontSize: 'var(--font-size-xs)'
  },
  headerRow: {
    display: 'grid',
    alignItems: 'center',
    backgroundColor: 'var(--color-neutral-700)',
    color: '#fff',
    fontWeight: 600,
    padding: '8px 0',
    gap: '4px',
    borderRadius: '4px 4px 0 0'
  },
  headerCell: {
    padding: '0 8px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  dataRow: {
    display: 'grid',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 0',
    borderBottom: '1px solid var(--color-neutral-200)',
    width: '100%',
    '&:nth-child(even)': {
      backgroundColor: 'var(--color-neutral-50, #fafafa)'
    }
  },
  inputCell: {
    padding: '0 8px'
  },
  statusCell: {
    padding: '0 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  inlineWarning: {
    color: 'var(--color-decline-500)',
    fontSize: 'var(--font-size-xxs, 0.625rem)',
    lineHeight: 1.2
  },
  statusInputError: {
    outline: '1px solid var(--color-decline-500)',
    borderRadius: 'var(--border-radius-sm)'
  },
  previewCell: {
    padding: '0 4px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  triggerCell: {
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    position: 'relative'
  },
  swatchButton: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    borderRadius: 'var(--border-radius-sm)',
    boxShadow: 'inset 0 0 0 1px var(--color-neutral-200)',
    flexShrink: 0
  },
  iconPreviewButton: {
    width: '28px',
    height: '28px',
    minWidth: '28px',
    borderRadius: 'var(--border-radius-sm)',
    border: '1px solid var(--color-neutral-200)',
    color: 'var(--text-color-subtle)',
    flexShrink: 0
  },
  swatchPopover: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    zIndex: 10
  },
  imageCell: {
    padding: '0 8px',
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    alignItems: 'center'
  },
  imageModeSelect: {
    flex: '0 0 110px',
    minWidth: 0
  },
  imageInputField: {
    flex: '1 1 auto',
    minWidth: 0
  },
  cellCenter: {
    padding: '0 8px',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  duplicateWarning: {
    color: 'var(--color-warning-600, var(--color-warning-500))',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '6px'
  },
  dragItem: {
    padding: '0 !important',
    '& > div:first-child': {
      marginRight: '0 !important'
    }
  },
  emptyRow: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    padding: '16px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  footerControls: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginTop: '8px'
  },
  formatHint: {
    fontSize: 'var(--font-size-xxs, 0.625rem)',
    color: 'var(--text-color-subtle)',
    marginTop: '4px'
  }
}))

function StatusStyleOverridesEditor ({ value, onChange, emptyMessage }) {
  const classes = useStyles()
  const [colorPickerRow, setColorPickerRow] = useState(null)
  const [iconPickerRow, setIconPickerRow] = useState(null)

  const rows = useMemo(() => (Array.isArray(value) ? value : []), [value])

  const duplicateKeys = useMemo(() => findDuplicateStatusKeys(rows), [rows])

  const updateRows = useCallback((nextRows) => {
    onChange(withDerivedOrder(nextRows))
  }, [onChange])

  const handleFieldChange = useCallback((index, field, next) => {
    const updated = rows.map((row, i) => i === index ? { ...row, [field]: next } : row)
    updateRows(updated)
  }, [rows, updateRows])

  // Merge several fields into one row in a single update. Needed by the image
  // mode toggle which has to write the new field AND clear the other one in
  // the same tick — two sequential `handleFieldChange` calls would close over
  // the stale `rows` snapshot and drop the first update.
  const handleRowMerge = useCallback((index, partial) => {
    const updated = rows.map((row, i) => i === index ? { ...row, ...partial } : row)
    updateRows(updated)
  }, [rows, updateRows])

  const handleRemoveRow = useCallback((index) => {
    updateRows(rows.filter((_, i) => i !== index))
  }, [rows, updateRows])

  const handleAddRow = useCallback(() => {
    updateRows([...rows, makeBlankStatusStyleRow()])
  }, [rows, updateRows])

  const handleClearAll = useCallback(() => {
    onChange([])
  }, [onChange])

  const onMoveItem = useCallback((dragIndex, hoverIndex) => {
    const next = [...rows]
    const dragged = next[dragIndex]
    next.splice(dragIndex, 1)
    next.splice(hoverIndex, 0, dragged)
    updateRows(next)
  }, [rows, updateRows])

  const handleColorPickerChange = useCallback((index, colorResult) => {
    const next = colorResult?.rgb?.a === 1
      ? colorResult.hex
      : `rgba(${colorResult.rgb.r}, ${colorResult.rgb.g}, ${colorResult.rgb.b}, ${colorResult.rgb.a})`
    handleFieldChange(index, 'color', next)
  }, [handleFieldChange])

  const handleIconConfirm = useCallback((index, iconName) => {
    handleFieldChange(index, 'icon', iconName || '')
  }, [handleFieldChange])

  return (
    <div>
      <div className={classes.grid}>
        <div className={classes.headerRow} style={{ gridTemplateColumns: HEADER_GRID_COLS }}>
          <div className={classes.headerCell} />
          <div className={classes.headerCell} />
          <div className={classes.headerCell}>Status</div>
          <div className={classes.headerCell}>Color</div>
          <div className={classes.headerCell}>Icon</div>
          <div className={classes.headerCell}>Image</div>
          <div className={classes.headerCell} />
        </div>

        <DndProvider backend={HTML5Backend}>
          {rows.map((row, index) => {
            const isDuplicate = !!(row.status || '').trim() && duplicateKeys.has(row.status.trim())
            const swatchColor = row.color || 'transparent'
            const previewKey = (row.status || '').trim() || `__preview__:${row._rowId ?? index}`
            return (
              <DragAndDropListItem
                id={row._rowId ?? `row-${index}`}
                key={row._rowId ?? `row-${index}`}
                index={index}
                onMoveItem={onMoveItem}
                size='small'
                className={classes.dragItem}
              >
                <div className={classes.dataRow} style={{ gridTemplateColumns: `52px ${GRID_COLS}` }}>
                  <div className={classes.previewCell}>
                    <PlanStatusIcon
                      status={previewKey}
                      size={18}
                      statusStyleOverrides={{
                        [previewKey]: {
                          color: row.color,
                          icon: row.icon,
                          imageId: row.imageId,
                          imageUrl: row.imageUrl
                        }
                      }}
                    />
                  </div>
                  <div className={classes.statusCell}>
                    <SydInput
                      value={row.status || ''}
                      onChange={(e) => handleFieldChange(index, 'status', e.target.value)}
                      size='sm'
                      minWidth='0'
                      placeholder='e.g. On Track'
                      className={isDuplicate ? classes.statusInputError : undefined}
                    />
                    {isDuplicate ? (
                      <span className={classes.inlineWarning}>Duplicate status — last row wins</span>
                    ) : null}
                  </div>
                  <div className={classes.triggerCell}>
                    <ButtonBase
                      className={classes.swatchButton}
                      style={{ backgroundColor: swatchColor }}
                      onClick={() => setColorPickerRow(colorPickerRow === index ? null : index)}
                      title='Open color picker'
                    />
                    <SydInput
                      value={row.color || ''}
                      onChange={(e) => handleFieldChange(index, 'color', e.target.value)}
                      size='sm'
                      minWidth='0'
                      placeholder='var(--color-...) or #hex'
                    />
                    {colorPickerRow === index ? (
                      <div className={classes.swatchPopover}>
                        <ColorPickerDialog
                          value={row.color || ''}
                          onClick={() => setColorPickerRow(null)}
                          onChange={(colorResult) => handleColorPickerChange(index, colorResult)}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div className={classes.triggerCell}>
                    <ButtonBase
                      className={classes.iconPreviewButton}
                      onClick={() => setIconPickerRow(index)}
                      title='Pick an icon'
                    >
                      {row.icon ? <Icon name={row.icon} customSize='18px' /> : <span style={{ fontSize: '10px' }}>+</span>}
                    </ButtonBase>
                    <SydInput
                      value={row.icon || ''}
                      onChange={(e) => handleFieldChange(index, 'icon', e.target.value)}
                      size='sm'
                      minWidth='0'
                      placeholder='icon name'
                    />
                  </div>
                  <div className={classes.imageCell}>
                    <ImageFieldGroup
                      row={row}
                      onMerge={(partial) => handleRowMerge(index, partial)}
                      selectClassName={classes.imageModeSelect}
                      inputClassName={classes.imageInputField}
                    />
                  </div>
                  <div className={classes.cellCenter}>
                    <IconButton size='small' onClick={() => handleRemoveRow(index)} aria-label='Remove row'>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </div>
                </div>
              </DragAndDropListItem>
            )
          })}
        </DndProvider>

        {rows.length === 0 ? (
          <div className={classes.emptyRow}>
            {emptyMessage}
          </div>
        ) : null}
      </div>

      <IconPickerDialog
        open={iconPickerRow !== null}
        onClose={() => setIconPickerRow(null)}
        value={iconPickerRow !== null ? (rows[iconPickerRow]?.icon || null) : null}
        onConfirm={(iconName) => {
          if (iconPickerRow !== null) handleIconConfirm(iconPickerRow, iconName)
        }}
      />

      <div className={classes.formatHint}>
        Rows render top-to-bottom in the filter pill list. Drag the handle to reorder &mdash; the persisted <code>order</code> field is rewritten automatically. <code>Color</code> accepts CSS variables (e.g. <code>var(--color-decline-500)</code>), hex codes, or rgb/rgba values. <code>Image</code> switches between a Media ID (resolved via the media service) and an external image URL &mdash; switching modes clears the other field.
        {duplicateKeys.size > 0 ? (
          <span className={classes.duplicateWarning}>
            <WarningIcon fontSize='inherit' style={{ marginRight: '4px' }} />
            Duplicate status keys: {Array.from(duplicateKeys).join(', ')}
          </span>
        ) : null}
      </div>

      <div className={classes.footerControls}>
        <SydButton onClick={handleAddRow} icon='add' size='sm'>
          Add Status
        </SydButton>
        {rows.length > 0 ? (
          <SydButton onClick={handleClearAll} variant='secondary' size='sm'>
            Clear All
          </SydButton>
        ) : null}
      </div>
    </div>
  )
}

StatusStyleOverridesEditor.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  emptyMessage: PropTypes.string
}

StatusStyleOverridesEditor.defaultProps = {
  value: [],
  emptyMessage: 'No status style overrides configured. The built-in defaults will be used.'
}

export default StatusStyleOverridesEditor
